import { Controller } from "@hotwired/stimulus"

const CART_KEY = "atombytes_cart"

export default class extends Controller {
  static targets = [
    "serviceList", "otpSection", "otpInputs", "otpBox",
    "submitBtn", "verificationStatus", "sendOtpBtn", "verifyOtpBtn",
    "servicesField", "phoneField"
  ]

  connect() {
    this.otpVerified = false
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("btn--disabled")
    }
    this.renderServices()
  }

  renderServices() {
    if (!this.hasServiceListTarget) return

    const cart = this.getCart()
    if (cart.length === 0) {
      window.location.href = "/cart"
      return
    }

    this.serviceListTarget.innerHTML = cart.map(item => `
      <div class="checkout-service-item">
        <i class="${this.escapeHtml(item.icon)}"></i>
        <span>${this.escapeHtml(item.name)}</span>
      </div>
    `).join("")

    if (this.hasServicesFieldTarget) {
      this.servicesFieldTarget.value = JSON.stringify(cart)
    }
  }

  async sendOtp() {
    const phone = this.phoneFieldTarget.value.trim()
    if (!phone || phone.length < 7) {
      this.showToast("Please enter a valid phone number", "alert")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.sendOtpBtnTarget.disabled = true
    this.sendOtpBtnTarget.textContent = "Sending…"

    try {
      const response = await fetch("/cart/send_otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ phone_number: phone })
      })
      const data = await response.json()

      if (response.ok) {
        this.otpInputsTarget.style.display = "block"
        this.showToast("Verification code sent!")
        setTimeout(() => {
          const firstBox = this.otpBoxTargets[0]
          if (firstBox) firstBox.focus()
        }, 100)
      } else if (data.reason === "locked_out") {
        this.showToast(this.lockedOutMessage(data.retry_after), "alert")
      } else {
        this.showToast(data.message || "Failed to send code", "alert")
      }
    } catch {
      this.showToast("Something went wrong. Please try again.", "alert")
    } finally {
      this.sendOtpBtnTarget.disabled = false
      this.sendOtpBtnTarget.innerHTML = '<i class="fas fa-paper-plane"></i> Send Verification Code'
    }
  }

  otpInput(event) {
    const input = event.currentTarget
    const digits = input.value.replace(/\D/g, "")

    if (digits.length > 1) {
      this.fillOtpBoxes(digits)
      return
    }

    input.value = digits
    const index = this.otpBoxTargets.indexOf(input)

    if (digits.length === 1 && index < this.otpBoxTargets.length - 1) {
      this.otpBoxTargets[index + 1].focus()
    }
  }

  otpKeydown(event) {
    if (event.key !== "Backspace") return

    const input = event.currentTarget
    if (input.value !== "") return

    const index = this.otpBoxTargets.indexOf(input)
    const previous = this.otpBoxTargets[index - 1]
    if (!previous) return

    event.preventDefault()
    previous.value = ""
    previous.focus()
  }

  otpPaste(event) {
    event.preventDefault()
    const digits = (event.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "")
    if (digits) this.fillOtpBoxes(digits)
  }

  fillOtpBoxes(digits) {
    const chars = digits.slice(0, this.otpBoxTargets.length).split("")
    this.otpBoxTargets.forEach((box, i) => { box.value = chars[i] || "" })

    const nextEmpty = this.otpBoxTargets.find(box => box.value === "")
    const focusTarget = nextEmpty || this.otpBoxTargets[this.otpBoxTargets.length - 1]
    focusTarget.focus()
  }

  verifyOtp() {
    const otp = this.otpBoxTargets.map(box => box.value).join("")

    if (otp.length !== 6) {
      this.showToast("Please enter the complete 6-digit code", "alert")
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.verifyOtpBtnTarget.disabled = true
    this.verifyOtpBtnTarget.textContent = "Verifying…"

    fetch("/cart/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ otp_code: otp })
    })
      .then(response => response.json())
      .then(data => {
        if (data.verified) {
          this.otpVerified = true
          this.otpSectionTarget.innerHTML = `
            <div class="verification-status verified">
              <i class="fas fa-check-circle"></i> Identity confirmed successfully
            </div>
          `
          if (this.hasSubmitBtnTarget) {
            this.submitBtnTarget.disabled = false
            this.submitBtnTarget.classList.remove("btn--disabled")
          }
          this.showToast("Identity confirmed!")
        } else if (data.reason === "locked_out") {
          this.showToast(this.lockedOutMessage(data.retry_after), "alert")
        } else {
          this.showToast(data.message || "Verification failed", "alert")
        }
      })
      .catch(() => {
        this.showToast("Verification failed. Please try again.", "alert")
      })
      .finally(() => {
        this.verifyOtpBtnTarget.disabled = false
        this.verifyOtpBtnTarget.textContent = "Verify Code"
      })
  }

  submit(event) {
    if (!this.otpVerified) {
      event.preventDefault()
      this.showToast("Please verify your phone number first", "alert")
      return
    }

    const cart = this.getCart()
    if (this.hasServicesFieldTarget) {
      this.servicesFieldTarget.value = JSON.stringify(cart)
    }
  }

  clearCart() {
    localStorage.removeItem(CART_KEY)
    const countElements = document.querySelectorAll('[data-cart-target="count"]')
    countElements.forEach(el => {
      el.textContent = "0"
      el.style.display = "none"
    })
  }

  getCart() {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY) || "[]")
    } catch {
      return []
    }
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  lockedOutMessage(retryAfter) {
    if (!retryAfter || retryAfter <= 0) return "Too many failed attempts. Please try again later."
    const hours = Math.floor(retryAfter / 3600)
    const minutes = Math.ceil((retryAfter % 3600) / 60)
    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    return `Too many failed attempts. Please try again in ${parts.join(" ")}.`
  }

  showToast(message, type = "notice") {
    let toast = document.getElementById("cart-toast")
    if (!toast) {
      toast = document.createElement("div")
      toast.id = "cart-toast"
      toast.innerHTML = '<div class="toast__content"><i class="toast__icon"></i><span></span></div>'
      document.body.appendChild(toast)
    }

    const icon = type === "alert" ? "fa-circle-exclamation" : "fa-check-circle"
    toast.querySelector(".toast__icon").className = `fas ${icon} toast__icon`
    toast.querySelector("span").textContent = message
    toast.className = `toast toast--${type} show`
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => toast.classList.remove("show"), 3000)
  }
}
